import { useEffect, useRef, useState } from 'react';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import RangeInput from '@ui/range-input/range-input';
import classes from './scale-slider.module.css';
import OptionalComponent from '@ui/optional-component/optional-component';

interface ScaleSliderProps {
  value: number;
  min?: number;
  max?: number;
  label?: string;
  inputLabel?: string;
  onChange: (value: number) => void;
}

// "Scale" row from the Figma Image/Logo panel spec: label + slider + editable percentage readout
export default function ScaleSlider({ value, min = 1, max = 500, onChange, label, inputLabel }: ScaleSliderProps) {
  // local display state (same pattern as NumberField) so the field can be fully cleared and
  // retyped instead of the controlled `value` prop snapping it back on every keystroke
  const [displayValue, setDisplayValue] = useState(value.toString());
  const isFocused = useRef(false);

  useEffect(() => {
    if (!isFocused.current) setDisplayValue(value.toString());
  }, [value]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      setDisplayValue('');
      return;
    }
    if (!/^\d*$/.test(raw)) return;
    const clamped = Math.min(max, Math.max(min, Number(raw)));
    setDisplayValue(String(clamped));
    onChange(clamped);
  };

  return (
    <Container
      className="flex gap-8 ai-center width-100-percent"
      background="var(--global-cont-1)"
      border="1px solid var(--global-opacity-white-5-percent)"
      borderRadius="12px"
      padding="12px 12px 12px 16px"
    >
      <OptionalComponent display={!!label}>
        <Text className="S2" color="var(--global-ico-text-3)" style={{ whiteSpace: 'nowrap' }}>
          {label}
        </Text>
      </OptionalComponent>
      <Container className="flex flex-1 gap-8 ai-center jc-flex-end" minWidth="0">
        <Container className="flex-1">
          <RangeInput
            min={min}
            max={max}
            value={value}
            onChange={onChange}
            fillColor="var(--project-theme-color-primary, var(--local-bttn-prim-default-bg))"
            trackColor="var(--global-ico-text-5)"
          />
        </Container>
        <Container
          className="flex ai-center jc-center gap-2 flex-shrink-0"
          border="1px solid var(--global-ico-text-3)"
          borderRadius="16px"
          padding="8px 12px"
        >
          <input
            type="number"
            aria-label="Scale percent"
            min={min}
            max={max}
            value={displayValue}
            onChange={handleTextChange}
            onFocus={() => {
              isFocused.current = true;
            }}
            onBlur={() => {
              isFocused.current = false;
              if (displayValue === '') setDisplayValue(value.toString());
            }}
            style={{
              width: `${String(max).length}ch`,
              minWidth: 0,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              padding: 0,
              textAlign: 'right',
              color: 'var(--global-ico-text-1)',
            }}
            className={`${classes.numberInput} P2`}
          />
          <OptionalComponent display={!!inputLabel}>
            <Text className="P2" color="var(--global-ico-text-1)">
              {inputLabel}
            </Text>
          </OptionalComponent>
        </Container>
      </Container>
    </Container>
  );
}
